import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { API_BASE, postData } from "../Services/Api";
import { useAuth } from "../Context/AuthContext";
import "./SpecialOfferPage.css";

/*
 * Marketing special-offer link.
 *
 * A visitor opens /special-offer/{token} and sees the offer price without
 * logging in. Registration and login happen on this page, so the visitor is
 * never sent away and never has to open the link a second time. The payment
 * is then started with the token, which lets the backend record who paid
 * through which marketing link.
 *
 * This page is self-contained on purpose: it reuses the existing register API
 * and the existing auth context, and does not modify the shared Register or
 * Login pages.
 */

/* The password rule the backend enforces is min:8, so match it here. */
const registerSchema = Yup.object().shape({
  first_name: Yup.string()
    .matches(/^[A-Za-z]+$/, "Only letters allowed")
    .required("First name is required"),
  last_name: Yup.string()
    .matches(/^[A-Za-z]+$/, "Only letters allowed")
    .required("Last name is required"),
  username: Yup.string().required("Username is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string().required("Phone number is required"),
  company: Yup.string().required("Company name is required"),
  country: Yup.string().required("Country is required"),
  state: Yup.string().required("State is required"),
  password: Yup.string()
    .min(8, "Minimum 8 characters")
    .required("Password is required"),
  password_confirmation: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Confirm your password"),
});

const loginSchema = Yup.object().shape({
  username: Yup.string().required("Username is required"),
  password: Yup.string().required("Password is required"),
});

const registerInitialValues = {
  first_name: "",
  last_name: "",
  username: "",
  email: "",
  phone: "",
  company: "",
  country: "",
  state: "",
  password: "",
  password_confirmation: "",
};

const loginInitialValues = {
  username: "",
  password: "",
};

/*
 * Styles for the register/login step live here rather than in
 * SpecialOfferPage.css so this feature stays contained in one file.
 * Colours follow the tokens already used by the offer card.
 */
const authStyles = `
.offer-note {
  margin: 12px 0 0;
  font-size: 13px;
  line-height: 1.6;
  color: #666a7a;
  text-align: center;
}

.offer-auth {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.offer-auth-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 18px;
}

.offer-auth-tabs button {
  flex: 1;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #666a7a;
  background: #f7f7f9;
  border: 1px solid #e5e5eb;
  border-radius: 8px;
  cursor: pointer;
}

.offer-auth-tabs button.is-active {
  color: #5d4ccf;
  background: #f0eeff;
  border-color: #5d4ccf;
}

.offer-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.offer-field-row {
  display: flex;
  gap: 12px;
}

.offer-field {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.offer-field label {
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #1c1c26;
}

.offer-field input {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  color: #1c1c26;
  background: #ffffff;
  border: 1px solid #e5e5eb;
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;
}

.offer-field input:focus {
  border-color: #5d4ccf;
  box-shadow: 0 0 0 3px #f0eeff;
}

.offer-field-error {
  margin-top: 4px;
  font-size: 11px;
  color: #d1394a;
}

.offer-auth .offer-btn {
  margin-top: 6px;
}

@media (max-width: 460px) {
  .offer-field-row {
    flex-direction: column;
  }
}
`;

export default function SpecialOfferPage() {
  const { token } = useParams();

  const { user, authLoading, login } = useAuth();

  const formRef = useRef(null);
  const authRef = useRef(null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("register");

  const [paying, setPaying] = useState(false);

  /*
   * Captured from the login response so the payment form has a user id even
   * before the auth context finishes re-rendering.
   */
  const [authUserId, setAuthUserId] = useState(null);

  useEffect(() => {
    const loadOffer = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await fetch(
          `${API_BASE}/special-offer/${encodeURIComponent(token)}`,
          {
            method: "GET",
            headers: { Accept: "application/json" },
          }
        );

        const result = await response.json();

        if (!response.ok || result.status !== true) {
          throw new Error(result.message || "Unable to load special offer.");
        }

        setData(result);
      } catch (err) {
        setError(err.message || "Unable to load special offer.");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      loadOffer();
    }
  }, [token]);

  /*
   * Submit the hidden form only after React has rendered it. A real form POST
   * is required because the API answers with the gateway's auto-submitting
   * page rather than JSON.
   */
  useEffect(() => {
    if (paying && formRef.current) {
      formRef.current.submit();
    }
  }, [paying]);

  const formatPrice = (value) =>
    Number(value || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    });

  if (loading) {
    return (
      <div className="offer-page">
        <div className="offer-status">
          <div className="offer-spinner" />
          <p>Loading your offer…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="offer-page">
        <div className="offer-status">
          <p className="offer-status-title">Offer Unavailable</p>
          <p className="offer-status-text">{error}</p>
          <a href="/" className="offer-btn offer-btn-secondary">
            Go to Home
          </a>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const isBundle = data.offer_type === "bundle";
  const isCourse = data.offer_type === "course";

  const offer = data.offer || {};
  const bundle = data.bundle || {};
  const course = data.course || {};

  const courses = Array.isArray(data.courses) ? data.courses : [];

  const normalPrice = isBundle
    ? Number(offer.normal_bundle_price || bundle.offer_price || 0)
    : Number(offer.normal_price || course.course_price || 0);

  const specialPrice = Number(offer.special_price || 0);

  const saving = normalPrice > specialPrice ? normalPrice - specialPrice : 0;

  const expiryText = offer.expiry_date
    ? new Date(offer.expiry_date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

  const title = isBundle ? bundle.offer_name : course.course_name;
  const description = isBundle ? bundle.description : course.course_short_desc;

  const payingUserId = user?.id || authUserId || "";

  /*
   * Already logged in: go straight to the gateway.
   * Otherwise open the register/login step on this same page.
   */
  const handlePayNow = () => {
    if (authLoading) {
      return;
    }

    if (!payingUserId) {
      setShowAuth(true);

      window.setTimeout(() => {
        authRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 50);

      return;
    }

    setPaying(true);
  };

  /* Register, then sign the new user in and continue to payment. */
  const handleRegister = async (values, { setSubmitting }) => {
    try {
      const payload = {
        ...values,
        fullname: `${values.first_name} ${values.last_name}`,
      };

      const { data: result, status } = await postData("register", payload);

      if (status === 409) {
        toast.error(result.message);
        return;
      }

      if (status === 422) {
        Object.values(result.errors || {}).forEach((messages) => {
          toast.error(messages[0]);
        });
        return;
      }

      if (!result.status) {
        toast.error(result.message || "Something went wrong");
        return;
      }

      toast.success(result.message || "Registration successful!");

      const session = await login({
        username: values.username,
        password: values.password,
      });

      setAuthUserId(session?.user?.id || null);
      setPaying(true);
    } catch (err) {
      /*
       * The account may exist even when the auto-login fails, so point the
       * visitor at the login tab instead of asking them to register again.
       */
      toast.error(
        err.message || "Registered, but sign-in failed. Please log in."
      );

      setAuthMode("login");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = async (values, { setSubmitting }) => {
    try {
      const session = await login(values);

      setAuthUserId(session?.user?.id || null);
      setPaying(true);
    } catch (err) {
      toast.error(err.message || "Invalid credentials");
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (name, label, type = "text") => (
    <div className="offer-field">
      <label htmlFor={`offer-${name}`}>{label}</label>
      <Field id={`offer-${name}`} name={name} type={type} />
      <ErrorMessage name={name} component="span" className="offer-field-error" />
    </div>
  );

  return (
    <main className="offer-page">
      <style>{authStyles}</style>

      <div className="offer-card">
        <div className="offer-header">
          <span className="offer-tag">Special Offer</span>
          <h1>{title}</h1>
          {description && (
            <p
              className="offer-desc"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
        </div>

        <div className="offer-price-row">
          <div>
            <span className="offer-price-label">Price</span>
            <div className="offer-price">₹{formatPrice(specialPrice)}</div>
          </div>

          {normalPrice > specialPrice && (
            <div className="offer-price-old">
              <span>Normal price</span>
              <strong>₹{formatPrice(normalPrice)}</strong>
            </div>
          )}
        </div>

        {saving > 0 && (
          <p className="offer-saving">You save ₹{formatPrice(saving)}</p>
        )}

        {expiryText && <p className="offer-expiry">Valid till {expiryText}</p>}

        {isBundle && courses.length > 0 && (
          <div className="offer-section">
            <h2>Courses included ({courses.length})</h2>
            <ul className="offer-list">
              {courses.map((item) => (
                <li key={item.id}>
                  <span>{item.course_name}</span>
                  {item.course_hrs && <em>{item.course_hrs}</em>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {isCourse && (
          <div className="offer-section">
            <ul className="offer-list offer-list-plain">
              {course.course_short_name && (
                <li>
                  <span>Course code</span>
                  <strong>{course.course_short_name}</strong>
                </li>
              )}
              {course.course_hrs && (
                <li>
                  <span>Duration</span>
                  <strong>{course.course_hrs}</strong>
                </li>
              )}
            </ul>
          </div>
        )}

        <button
          type="button"
          className="offer-btn"
          onClick={handlePayNow}
          disabled={authLoading || paying}
        >
          {paying
            ? "Redirecting to payment…"
            : payingUserId
            ? `Pay ₹${formatPrice(specialPrice)} now`
            : `Register & pay ₹${formatPrice(specialPrice)}`}
        </button>

        {!authLoading && !payingUserId && !showAuth && (
          <p className="offer-note">
            Create an account to claim this price. It only takes a minute and
            you stay on this page.
          </p>
        )}

        {/* Register / login happens here so the visitor never leaves. */}
        {!payingUserId && showAuth && !paying && (
          <div className="offer-auth" ref={authRef}>
            <div className="offer-auth-tabs">
              <button
                type="button"
                className={authMode === "register" ? "is-active" : ""}
                onClick={() => setAuthMode("register")}
              >
                New user
              </button>
              <button
                type="button"
                className={authMode === "login" ? "is-active" : ""}
                onClick={() => setAuthMode("login")}
              >
                Already registered
              </button>
            </div>

            {authMode === "register" ? (
              <Formik
                initialValues={registerInitialValues}
                validationSchema={registerSchema}
                onSubmit={handleRegister}
              >
                {({ isSubmitting }) => (
                  <Form className="offer-form">
                    <div className="offer-field-row">
                      {renderField("first_name", "First name")}
                      {renderField("last_name", "Last name")}
                    </div>

                    <div className="offer-field-row">
                      {renderField("username", "Username")}
                      {renderField("email", "Email", "email")}
                    </div>

                    <div className="offer-field-row">
                      {renderField("phone", "Phone")}
                      {renderField("company", "Company")}
                    </div>

                    <div className="offer-field-row">
                      {renderField("country", "Country")}
                      {renderField("state", "State")}
                    </div>

                    <div className="offer-field-row">
                      {renderField("password", "Password", "password")}
                      {renderField(
                        "password_confirmation",
                        "Confirm password",
                        "password"
                      )}
                    </div>

                    <button
                      type="submit"
                      className="offer-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? "Creating your account…"
                        : `Register & pay ₹${formatPrice(specialPrice)}`}
                    </button>
                  </Form>
                )}
              </Formik>
            ) : (
              <Formik
                initialValues={loginInitialValues}
                validationSchema={loginSchema}
                onSubmit={handleLogin}
              >
                {({ isSubmitting }) => (
                  <Form className="offer-form">
                    {renderField("username", "Username")}
                    {renderField("password", "Password", "password")}

                    <button
                      type="submit"
                      className="offer-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? "Signing you in…"
                        : `Log in & pay ₹${formatPrice(specialPrice)}`}
                    </button>
                  </Form>
                )}
              </Formik>
            )}
          </div>
        )}

        {/*
          The amount is never sent from the browser. Laravel reads
          special_offer_tokens.special_price for this token.
        */}
        {paying && (
          <form
            ref={formRef}
            action={`${API_BASE}/special-offer/payment/pre-pay-process`}
            method="POST"
            style={{ display: "none" }}
          >
            <input type="hidden" name="token" value={token} />
            <input type="hidden" name="user_id" value={payingUserId} />
          </form>
        )}
      </div>
    </main>
  );
}
